import React, { Component, useEffect, type ReactNode } from 'react';
import Link from '@atlaskit/link';
import type { MaybeChoreographedComponentProps } from '@atlassian/jira-choreographer-services/src/types.tsx';
import { getErrorHash } from '@atlassian/jira-errors-handling/src/utils/error-hash.tsx';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import FlagGroup from '@atlassian/jira-flags/src/common/ui/components/group-flag';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { getUserLocation } from '@atlassian/jira-platform-router-utils/src/common/utils/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { ErrorDetails } from './error-details/ErrorDetails.tsx';
import messages from './messages.tsx';

const flagId = 'error-flag';

const reloadPage = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
	fireUIAnalytics(analyticsEvent, 'clicked button', 'errorFlagReloadPage');
	// @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.location.reload(true);
};

type Props = {
	error: Error | null;
	onDismiss?: () => void;
	intl: IntlShape;
} & Partial<MaybeChoreographedComponentProps>;

type State = {
	isDismissed: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
class ErrorFlagGroupInternal extends Component<Props, State> {
	state = {
		isDismissed: false,
	};

	componentWillUnmount() {
		if (this.props.onDismiss) {
			this.props.onDismiss();
		}
	}

	onDismiss = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'dismissed flag', 'errorFlag');

		this.setState({
			isDismissed: true,
		});

		if (this.props.onDismiss) {
			this.props.onDismiss();
		}
	};

	render() {
		if (this.state.isDismissed) {
			return null;
		}

		const { formatMessage } = this.props.intl;
		let description: ReactNode | string = formatMessage(messages.description, {
			link: (chunks: ReactNode[]) => (
				<Link
					href="https://support.atlassian.com/contact"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'clicked link', 'errorFlagContactSupport');
					}}
				>
					{chunks}
				</Link>
			),
		});

		if (this.props.error) {
			description = <ErrorDetails description={description} error={this.props.error} />;
		}

		return (
			<FlagGroup onDismissed={this.onDismiss}>
				<ErrorFlag
					id={flagId}
					title={formatMessage(messages.header)}
					description={description}
					messageId={
						this.props.messageId ??
						`error-boundary-flag-renderer.ui.error-flag.error-flag.${flagId}`
					}
					messageType={this.props.messageType ?? 'transactional'}
					actions={[
						{
							content: formatMessage(messages.reloadPageButton),
							onClick: reloadPage,
						},
					]}
				/>
			</FlagGroup>
		);
	}
}

type ErrorFlagGroupProps = Omit<Props, 'intl'>;

export default function ErrorFlagGroupWithAnalytics(props: ErrorFlagGroupProps) {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { error } = props;

	useEffect(() => {
		const event = createAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'errorFlag',
		});

		fireTrackAnalytics(event, 'errorFlag rendered', {
			errorHash: error ? getErrorHash(error) : 'null',
			userLocation: getUserLocation(),
		});
	}, [createAnalyticsEvent, error]);

	return <ErrorFlagGroupInternal {...props} intl={intl} />;
}
